import fetchGuideCollections from 'actions/guide_performance/fetch_guide_collections';
import isFeatureEnabled from 'actions/lib/is_feature_enabled';
import UserLogin from 'models/location/user_login';
import { ComplianceAdminRole, SystemAdminRole, TeamManagerRole } from 'models/role';
import OrganizationConverter from 'scripts/application/dto_converters/organization_converter';
import { NotExistError } from 'scripts/application/lib/error';
import { handleCommonErrors } from 'scripts/application/lib/gateway_error_silent_handler';
import analytics from 'scripts/lib/analytics';

const ONBOARDING_REDIRECT_ROLE_IDS = [SystemAdminRole.id, ComplianceAdminRole.id, TeamManagerRole.id];

export default class RequestOrganization {
  constructor(context) {
    this.context = context;
  }

  run() {
    const { auth, organization: organizationStore } = this.context.stores;
    const { organizations } = this.context.gateways;

    const orgId = auth.get().getOrgId();
    organizationStore.setLoading();

    let redirected = false;

    return organizations
      .fetch({ orgId })
      .then(dto => {
        const organization = OrganizationConverter.fromDto(dto);
        organizationStore.set(organization);
        analytics.updateUser({ loginDomain: organization.loginDomain });

        if (this.redirectSelfServeOnboardingOrg()) {
          redirected = true;
          return;
        }

        return this.updateSidekickStatus(organization);
      })
      .then(() => {
        if (redirected) return;
        fetchGuideCollections(this.context);
      })
      .catch(err => {
        handleCommonErrors(this.context, err);
      })
      .finally(() => {
        organizationStore.resetLoading();
      });
  }

  redirectSelfServeOnboardingOrg() {
    // Route self-serve onboarding merchants to the glados onboarding home on
    // login. Eligibility is flag-based, not org-type based: `onboardingHome` is
    // the eng rollout flag, and `shopifySourcedDeal` (set by ps-org-creator for
    // source === 'shopify') or `smbSelfServe` (set per-org in org manager for an
    // inked SMB self-serve deal) marks an eligible org. Shopify self-serve orgs
    // are created as TRIAL — not OrganizationType.STANDALONE — so the previous
    // gate on org type never fired for the intended audience.
    const rolloutEnabled = isFeatureEnabled(this.context, 'onboardingHome');
    const eligible =
      isFeatureEnabled(this.context, 'shopifySourcedDeal') || isFeatureEnabled(this.context, 'smbSelfServe');

    if (!rolloutEnabled || !eligible) {
      return false;
    }

    const currentLocation = this.context.stores.currentLocation.get();
    const isLoginFlow = currentLocation instanceof UserLogin || currentLocation.isFromSso;

    if (!isLoginFlow || !this.hasOnboardingRedirectRole()) {
      return false;
    }

    this.context.router.navigateToPath(this.onboardingRedirectPath());
    return true;
  }

  onboardingRedirectPath() {
    // Rollout and eligibility are already confirmed by
    // redirectSelfServeOnboardingOrg. Land the merchant on the recoverable
    // onboarding home until onboarding is complete; both paths are served by
    // glados on the same origin, so this is a full-window route with the
    // session cookie inherited — no hand-off work.
    if (!this.hasCompletedOnboarding()) {
      return '/sidekick/getting-started';
    }

    return '/sidekick/communication-sessions';
  }

  hasCompletedOnboarding() {
    // No server-side "onboarding complete" signal is surfaced to agent-desktop
    // yet (the planned `Organization.GoLiveDate` write was dropped, and there is
    // no gatekeeper completion flag). Until one exists, treat onboarding as
    // never-complete so flagged merchants always land on the recoverable
    // onboarding home; the home's checklist reflects real progress and the nav
    // provides access to conversations. This is the single seam to update once a
    // coarse completion signal (gatekeeper/org) is available.
    return false;
  }

  hasOnboardingRedirectRole() {
    const roleIds = this.context.stores.auth.get().getRoleIds() || [];
    return roleIds.some(id => ONBOARDING_REDIRECT_ROLE_IDS.includes(id));
  }

  updateSidekickStatus(organization) {
    if (!isFeatureEnabled(this.context, 'viewGuidePerformance') && !isFeatureEnabled(this.context, 'answers:admin')) {
      return;
    }

    const { organization: organizationStore } = this.context.stores;
    const { sidekickOrganization: gateway } = this.context.gateways;

    return gateway
      .fetch()
      .then(() => {
        organization.setSidekickEnabled(true);
        organizationStore.set(organization);
      })
      .catch(err => {
        if (!(err instanceof NotExistError)) {
          throw err;
        }
      });
  }
}
